import { useCallback, useEffect, useState } from "react";
import { DeleteTwoTone, EditTwoTone, EyeOutlined } from "@ant-design/icons";
import { Dropdown, Menu, message, Modal, Popconfirm, Table, Typography } from "antd";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { deleteData, getData } from "apiServices/common";
import { SAMPLE_TYPE_DELETE_ENDPOINT, SAMPLE_TYPE_LIST_ENDPOINT } from "apiServices/API_ENDPOINTS";
import {
  SAMPLE_TYPE_CREATE_PERMISSION,
  SAMPLE_TYPE_DELETE_PERMISSION,
  SAMPLE_TYPE_EDIT_PERMISSION,
  SAMPLE_TYPE_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import SampleTypeForm from "./SampleTypeForm";

const { Title } = Typography;

const SampleTypeConfig = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = !!showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath, location]);

  const getAllSampleTypes = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};
      setLoading(true);
      const response = await getData(`${SAMPLE_TYPE_LIST_ENDPOINT}?page=${currentPage}`, false, {
        ...(name && { name }),
      });
      setLoading(false);

      if (response?.data?.code === 200) {
        const data = response?.data?.data;
        setMetaData({
          defaultPageSize: data?.per_page,
          current: data?.current_page,
          defaultCurrent: data?.current_page,
          total: data?.total,
        });
        setTableData(data?.data ?? []);
      } else {
        message.error("Error loading types of sample");
      }
    },
    [currentPage],
  );

  useEffect(() => {
    getAllSampleTypes(filterData);
  }, [refresh, filterData, getAllSampleTypes]);

  const openModal = (record = null, mode = "create") => {
    setSelectedItem(record);
    setIsEdit(mode === "edit");
    setIsView(mode === "view");
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setIsView(false);
    setSelectedItem(null);
  };

  const refetch = () => setRefresh((prev) => prev + 1);

  const deleteSelected = async (id) => {
    setLoading(true);
    const response = await deleteData(`${SAMPLE_TYPE_DELETE_ENDPOINT}/${id}`);
    setLoading(false);
    if (response?.code === 200 || response?.data?.code === 200) {
      message.success("Type of Sample deleted successfully");
      refetch();
    }
  };

  const getModalTitle = () => {
    if (isView) return "View Type of Sample";
    if (isEdit) return "Edit Type of Sample";
    return "Create Type of Sample";
  };

  const tableColumns = [
    { title: "Name", dataIndex: "name", key: "name", width: "25%" },
    { title: "Description", dataIndex: "description", key: "description", width: "40%", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status) => <span>{String(status) === "1" ? "Active" : "Inactive"}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <Menu
              items={[
                {
                  label: (
                    <span onClick={() => openModal(record, "view")} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <EyeOutlined />
                      View
                    </span>
                  ),
                  key: "view",
                  disabled: !hasPermission([SAMPLE_TYPE_VIEW_PERMISSION]),
                },
                {
                  label: (
                    <span onClick={() => openModal(record, "edit")} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <EditTwoTone />
                      Edit
                    </span>
                  ),
                  key: "edit",
                  disabled: !hasPermission([SAMPLE_TYPE_EDIT_PERMISSION]),
                },
                {
                  label: (
                    <Popconfirm title="Are you sure you want to delete?" onConfirm={() => deleteSelected(record?.id)}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5, color: "red" }}>
                        <DeleteTwoTone twoToneColor="red" />
                        Delete
                      </span>
                    </Popconfirm>
                  ),
                  key: "delete",
                  disabled: !hasPermission([SAMPLE_TYPE_DELETE_PERMISSION]),
                },
              ]}
            />
          }
        />
      ),
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={() => openModal()}
                disabled={!hasPermission([SAMPLE_TYPE_CREATE_PERMISSION])}
                addButtonText={hasPermission([SAMPLE_TYPE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Type of Sample</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllSampleTypes}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
            <div className="table" style={{ marginTop: 10 }}>
              <Table
                loading={loading}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: setCurrentPage,
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={getModalTitle()}
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        destroyOnClose
      >
        <SampleTypeForm
          isEdit={isEdit}
          isView={isView}
          onCloseModal={onCloseModal}
          refetch={refetch}
          selectedItem={selectedItem}
        />
      </Modal>
    </>
  );
};

export default SampleTypeConfig;
